<script setup lang="ts">
import type { Component } from 'vue';
import { useBanners } from '~/composables/apostatudo/use-banners';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';

const { isLayoutMobileOrTablet: isMobile } = useLayout();
const { getLoadingStrategy } = useImageLoading();
const { getResponsiveBannerUrlWithWidth } = useResponsiveBannerUrl();
const { openLogin, openRegister } = useAuth();
const { homeBanners, fetchBanners, hasHomeBanners } = useBanners();

const bannerContainer = ref<HTMLDivElement | null>(null);

// Intersection Observer para lazy loading dos banners
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (bannerContainer.value && !hasHomeBanners.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchBanners();
            observer?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      },
    );

    observer.observe(bannerContainer.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});

// Lazy load Swiper
const Swiper = shallowRef<Component | null>(null);
const SwiperSlide = shallowRef<Component | null>(null);
const swiperLoaded = ref(false);

onBeforeMount(async () => {
  const [swiperVue, swiperElement] = await Promise.all([
    import('swiper/vue'),
    import('swiper/element/bundle'),
  ]);

  Swiper.value = swiperVue.Swiper;
  SwiperSlide.value = swiperVue.SwiperSlide;
  swiperElement.register();
  swiperLoaded.value = true;
});

type Props = {
  isAboveFold?: boolean;
};

const { isAboveFold = false } = defineProps<Props>();

const itemsAboveFold = computed(() => (isAboveFold ? 2 : 0));

const showInformationSection = computed(() => {
  return homeBanners.value && homeBanners.value.length > 0;
});

// URL do próprio app vira path (navegação client-side); link externo — inclusive
// subdomínios como o encurtador go.apostatudo.bet.br — volta inteiro.
const handleActionURL = (linkUrl: string | null) => {
  return resolveAppLink(linkUrl);
};

// CTA de login/cadastro abre o modal de auth em vez de navegar de rota.
// Reaproveita handleActionURL pra extrair o pathname (ignorando host,
// query e prefixo de locale).
const resolveAuthCta = (
  linkUrl: string | null,
): 'login' | 'register' | null => {
  if (!linkUrl) return null;

  const path = handleActionURL(linkUrl).split('?')[0] ?? '';
  const last = path.replace(/\/+$/, '').split('/').pop();

  if (last === 'login') return 'login';
  if (last === 'register' || last === 'signup') return 'register';

  return null;
};

const handleBannerClick = (
  banner: { link_url: string | null },
  event: MouseEvent,
) => {
  const cleanPath = handleActionURL(banner.link_url).split('?')[0] ?? '';

  // Roletinha: abre o modal global (store) sem navegar de rota, igual ao menu.
  // A store gateia auth sozinha (deslogado vê só o login, sem o sheet).
  if (cleanPath.startsWith('/mini-games/')) {
    event.preventDefault();

    useRouletteStore().open();

    return;
  }

  const cta = resolveAuthCta(banner.link_url);

  if (!cta) return;

  event.preventDefault();
  cta === 'register' ? openRegister() : openLogin();
};

const isExternalLink = (linkUrl: string | null) => {
  return isExternalAppUrl(linkUrl);
};

// Loop sempre habilitado quando tiver slides suficientes
const shouldEnableLoop = computed(() => {
  return homeBanners.value.length >= 3;
});
</script>

<template>
  <!-- Skeleton/Loading state -->
  <div
    v-if="!showInformationSection && isMobile"
    ref="bannerContainer"
    class="banner-carousel-wrapper"
    style="min-height: 149px"
  >
    <div class="flex gap-[10px] justify-center">
      <!-- aspect-ratio 329/149: dimensões do banner mobile no Figma (329×149px) -->
      <BaseCommonSkeleton
        class="flex-shrink-0 rounded-[15px]"
        style="width: 83%; height: 149px; aspect-ratio: 329/149"
      />
    </div>
  </div>

  <div
    v-if="showInformationSection"
    ref="bannerContainer"
    class="banner-carousel-wrapper"
  >
    <div
      v-if="swiperLoaded"
      class="banner-slider"
    >
      <component
        :is="Swiper"
        :autoplay="{
          delay: 6000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }"
        :centered-slides="!isMobile"
        class="infoSwiper h-full"
        create-elements
        :loop="shouldEnableLoop"
        preload-images="false"
        :slides-per-group="1"
        :slides-per-view="1.2"
        :space-between="isMobile ? 10 : 4"
        :speed="500"
      >
        <component
          :is="SwiperSlide"
          v-for="(banner, index) in homeBanners"
          :key="banner.id"
          class="h-full"
        >
          <NuxtLink
            v-if="banner"
            class="block h-full"
            :external="isExternalLink(banner.link_url)"
            no-prefetch
            :target="isExternalLink(banner.link_url) ? '_blank' : '_self'"
            :to="handleActionURL(banner.link_url)"
            @click.capture="handleBannerClick(banner, $event)"
          >
            <!-- aspect-[329/149]: mobile (329×149px); md:aspect-[282/122]: desktop (282×122px) — dimensões Figma -->
            <img
              :alt="banner.filename || `Banner ${index}`"
              class="object-contain w-full h-full rounded-[15px] md:rounded-xl aspect-[329/149] md:aspect-[282/122]"
              height="149"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
              :src="getResponsiveBannerUrlWithWidth(banner.url)"
              width="329"
            />
          </NuxtLink>
        </component>
      </component>
    </div>
  </div>

  <!-- Placeholder invisível para Intersection Observer quando não há banners -->
  <div
    v-if="!showInformationSection && !isMobile"
    ref="bannerContainer"
    aria-hidden="true"
    class="invisible h-1"
  ></div>
</template>

<style scoped lang="scss">
.banner-carousel-wrapper {
  @apply select-none overflow-hidden mb-4 sm:mb-5 md:mb-8;
  min-height: 149px;
  margin-left: calc(-1 * var(--container-padding, 16px));
  margin-right: calc(-1 * var(--container-padding, 16px));

  @media (max-width: 1023px) {
    margin-left: 0;
  }

  @media (min-width: 1024px) {
    min-height: 122.46px;
  }
}

.banner-slider {
  @apply relative;
  width: 100%;
}

// Swiper customizado
.infoSwiper {
  @apply w-full;

  :deep(.swiper-wrapper) {
    align-items: center;
  }

  :deep(.swiper-button-next),
  :deep(.swiper-button-prev) {
    display: none;
  }

  :deep(.swiper-slide) {
    z-index: 1;
    transition: all 0.4s ease;
    border-radius: 0.75rem;
    overflow: hidden;

    @media (min-width: 1024px) {
      opacity: 0.4;
      transform: scaleY(0.82);
    }
  }

  :deep(.swiper-slide-active) {
    opacity: 1;
    z-index: 2;
    transform: scaleY(1);
  }
}
</style>
